import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { AuthService } from '@fc-core/services';
import { UploadChanges } from '../models/UploadChanges';

import type * as tus from 'tus-js-client';
import { environment } from 'src/environments/environment';

interface ActiveUpload {
  upload: tus.Upload;
  changes: WritableSignal<UploadChanges>;
}

@Injectable({
  providedIn: 'root',
})
export class FilesUploadsApiService {
  private readonly auth = inject(AuthService);
  private readonly activeUploads = new Map<number, ActiveUpload>();

  async uploadFile(
    file: File,
    slotId: number,
  ): Promise<{ slotId: number; changes: WritableSignal<UploadChanges> }> {
    const changes = signal<UploadChanges>({ type: 'progress', progress: 0 });
    const token = this.auth.token ?? '';
    const { Upload } = await import('tus-js-client');

    const upload = new Upload(file, {
      endpoint: environment.tus,
      retryDelays: [0, 1000, 3000, 5000],
      parallelUploads: 1,
      removeFingerprintOnSuccess: true,
      // Scope the resume fingerprint to this upload slot. The default
      // fingerprint is derived from the file alone, so a file that was
      // cancelled and then re-added would resume the previous upload URL,
      // whose server-side metadata still points at the old slot, and finalize
      // the bytes onto the wrong Attachment. A fresh slot means a fresh
      // identity; an in-session retry reuses the same slot and still resumes.
      fingerprint: async (f) =>
        ['tus', slotId, f.name, f.type, f.size, f.lastModified].join('-'),
      metadata: {
        filename: file.name,
        filetype: file.type,
        uploadedFileId: slotId.toString(),
      },
      headers: {
        Authorization: token,
      },
      onProgress: (bytesSent, bytesTotal) => {
        changes.set({ type: 'progress', progress: bytesSent / bytesTotal });
      },
      onSuccess: () => {
        changes.set({ type: 'success' });
        this.activeUploads.delete(slotId);
      },
      onError: (error: Error | tus.DetailedError) => {
        const detailedError = error as tus.DetailedError;
        if (detailedError?.originalResponse?.getBody()) {
          const message = JSON.parse(detailedError.originalResponse.getBody());
          changes.set({ type: 'error', error: new Error(message.message) });
        } else {
          changes.set({ type: 'error', error: new Error('Upload failed') });
        }
      },
    });

    this.activeUploads.set(slotId, { upload, changes });

    const previousUploads = await upload.findPreviousUploads();
    if (previousUploads.length) {
      upload.resumeFromPreviousUpload(previousUploads[0]);
    }

    upload.start();

    return { slotId, changes };
  }

  cancelUpload(slotId: number): boolean {
    const activeUpload = this.activeUploads.get(slotId);
    if (activeUpload) {
      this.terminateUpload(activeUpload.upload);
      this.activeUploads.delete(slotId);
      return true;
    }
    return false;
  }

  cancelAllUploads(): void {
    this.activeUploads.forEach(({ upload }) => this.terminateUpload(upload));
    this.activeUploads.clear();
  }

  // Pass `true` so the upload is deleted from tusd (freeing the orphaned
  // partial blob) and its fingerprint is dropped from local storage, so a
  // later re-add can never resume this cancelled upload. Fire-and-forget: the
  // DELETE is best-effort and must not block the cancel.
  private terminateUpload(upload: tus.Upload): void {
    upload.abort(true).catch((error) => {
      console.error('Failed to terminate cancelled upload:', error);
    });
  }

  hasActiveUploads(): boolean {
    return this.activeUploads.size > 0;
  }

  getActiveUploadsCount(): number {
    return this.activeUploads.size;
  }
}
